import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ResourceCard from "../components/ResourceCard";
import { publicService } from "../services/publicService";
import {
	clearSelectedResourceIds,
	getSelectedResources,
	getSelectedResourceIds,
	removeSelectedResource,
	upsertSelectedResource,
} from "../utils/inquirySelection";

export default function Inquiry() {
	const navigate = useNavigate();
	const [resources, setResources] = useState([]);
	const [loadingResources, setLoadingResources] = useState(true);
	const [form, setForm] = useState({
		name: "",
		email: "",
		company: "",
		message: "",
	});
	const [submitted, setSubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");
	const [selectedResourcesWithComments, setSelectedResourcesWithComments] =
		useState([]);
	const [editCommentModal, setEditCommentModal] = useState({
		open: false,
		resourceId: null,
		resourceName: "",
		comment: "",
	});

	const selectedResourceIds = useMemo(
		() => resources.map((r) => r.id),
		[resources],
	);

	const loadSelectedResources = async () => {
		const selectedEntries = getSelectedResources();
		setSelectedResourcesWithComments(selectedEntries);
		const selectedIds = getSelectedResourceIds();

		if (selectedIds.length === 0) {
			setResources([]);
			setLoadingResources(false);
			return;
		}

		try {
			setLoadingResources(true);
			const data = await publicService.getResourcesByIds(selectedIds);
			setResources(data || []);
		} catch {
			setResources([]);
			setError("Failed to load selected profiles.");
		} finally {
			setLoadingResources(false);
		}
	};

	useEffect(() => {
		void loadSelectedResources();
	}, []);

	const handleToggleSelect = (resourceId) => {
		removeSelectedResource(resourceId);
		setSelectedResourcesWithComments((prev) =>
			prev.filter((item) => item.id !== Number(resourceId)),
		);
		const selectedIds = getSelectedResourceIds();
		setResources((prev) =>
			prev.filter((resource) => selectedIds.includes(resource.id)),
		);
	};

	const handleOpenEditComment = (resource) => {
		const existingComment =
			selectedResourcesWithComments.find((item) => item.id === resource.id)
				?.comment || "";
		const resourceName =
			`${resource?.first_name || ""} ${resource?.last_name || ""}`.trim() ||
			resource?.role ||
			`Resource ID-${resource?.id}`;

		setEditCommentModal({
			open: true,
			resourceId: resource.id,
			resourceName,
			comment: existingComment,
		});
	};

	const handleCloseEditComment = () => {
		setEditCommentModal({
			open: false,
			resourceId: null,
			resourceName: "",
			comment: "",
		});
	};

	const handleSaveEditedComment = () => {
		const updatedComment = editCommentModal.comment.trim();
		if (!updatedComment) {
			setError("Comment is mandatory for each selected resource.");
			return;
		}

		const nextSelections = upsertSelectedResource(
			editCommentModal.resourceId,
			updatedComment,
		);
		setSelectedResourcesWithComments(nextSelections);
		setError("");
		handleCloseEditComment();
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (selectedResourceIds.length === 0) {
			setError(
				"Please select at least one profile before submitting an inquiry.",
			);
			return;
		}

		const selectedPayload = selectedResourcesWithComments.filter((item) =>
			selectedResourceIds.includes(item.id),
		);
		const hasMissingComments =
			selectedPayload.length !== selectedResourceIds.length ||
			selectedPayload.some((item) => !String(item.comment || "").trim());

		if (hasMissingComments) {
			setError(
				"Each selected resource requires a mandatory comment. Please reselect any resource missing a comment.",
			);
			return;
		}

		setSubmitting(true);
		setError("");

		try {
			const payload = {
				...form,
				resourceIds: selectedResourceIds,
				selectedResources: selectedPayload.map((item) => ({
					resourceId: item.id,
					comment: item.comment,
				})),
			};

			// Call validation endpoint instead of submit
			const result = await publicService.validateAndSendCode(payload);

			// Navigate to verification page with inquiry data
			navigate("/inquiry/verify", {
				state: {
					inquiryData: payload,
					submissionId: result.submissionId,
				},
			});
		} catch (err) {
			setError(err.message || "Failed to validate inquiry. Please try again.");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="min-h-screen flex flex-col bg-gray-50">
			<Navbar />
			<main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
				<div className="mb-8">
					<h1 className="text-3xl font-bold text-gray-900 mb-2">
						Resource Inquiry
					</h1>
					<p className="text-gray-600">
						Submit your inquiry with selected profiles and our team will contact
						you.
					</p>
				</div>

				{loadingResources ? (
					<div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-600">
						Loading selected resources...
					</div>
				) : selectedResourceIds.length === 0 && !submitted ? (
					<div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
						<h2 className="text-xl font-semibold text-gray-900 mb-2">
							No selected profiles
						</h2>
						<p className="text-gray-600 mb-5">
							Select one or more profiles from the directory to submit an
							inquiry.
						</p>
						<Link to="/resources" className="btn-primary">
							Go to Resource Directory
						</Link>
					</div>
				) : (
					<div className="space-y-8">
						<section>
							<div className="flex items-center justify-between mb-4">
								<h2 className="text-xl font-bold text-gray-900">
									Selected Profiles
								</h2>
								<span className="text-sm text-gray-600">
									{selectedResourceIds.length} selected
								</span>
							</div>

							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
								{resources.map((resource) => (
									<div key={resource.id} className="space-y-2">
										<ResourceCard
											resource={resource}
											selectable
											selected
											onToggleSelect={handleToggleSelect}
										/>
										<div className="bg-blue-50 border border-blue-100 rounded-lg p-3 text-xs text-blue-800">
											<span className="font-semibold">Comment:</span>{" "}
											{selectedResourcesWithComments.find(
												(item) => item.id === resource.id,
											)?.comment || "-"}
											<div className="mt-2">
												<button
													type="button"
													onClick={() => handleOpenEditComment(resource)}
													className="text-blue-700 hover:text-blue-900 font-medium"
												>
													Edit Comment
												</button>
											</div>
										</div>
									</div>
								))}
							</div>
						</section>

						<section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 max-w-3xl">
							<form onSubmit={handleSubmit} className="space-y-4">
								<h2 className="text-xl font-bold text-gray-900 mb-2">
									Send Inquiry
								</h2>
								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Name *
										</label>
										<input
											type="text"
											required
											value={form.name}
											onChange={(e) =>
												setForm((prev) => ({ ...prev, name: e.target.value }))
											}
											className="input-field"
											placeholder="Your full name"
										/>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Email *
										</label>
										<input
											type="email"
											required
											value={form.email}
											onChange={(e) =>
												setForm((prev) => ({
													...prev,
													email: e.target.value,
												}))
											}
											className="input-field"
											placeholder="your@email.com"
										/>
									</div>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Company *
									</label>
									<input
										type="text"
										required
										value={form.company}
										onChange={(e) =>
											setForm((prev) => ({
												...prev,
												company: e.target.value,
											}))
										}
										className="input-field"
										placeholder="Your company"
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Message *
									</label>
									<textarea
										required
										rows={5}
										value={form.message}
										onChange={(e) =>
											setForm((prev) => ({
												...prev,
												message: e.target.value,
											}))
										}
										className="input-field resize-none"
										placeholder="Tell us about your requirement for these selected resources"
									/>
								</div>

								{error && <p className="text-sm text-red-600">{error}</p>}

								<button
									type="submit"
									className="btn-primary w-full justify-center"
									disabled={submitting || selectedResourceIds.length === 0}
								>
									{submitting ? "Validating..." : "Continue to Verification"}
								</button>
							</form>
						</section>
					</div>
				)}
			</main>
			<Footer />
			{editCommentModal.open && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
					<div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-2">
							Edit Inquiry Comment
						</h3>
						<p className="text-sm text-gray-600 mb-4">
							{editCommentModal.resourceName}
						</p>
						<textarea
							value={editCommentModal.comment}
							onChange={(e) =>
								setEditCommentModal((prev) => ({
									...prev,
									comment: e.target.value,
								}))
							}
							rows={4}
							className="input-field resize-none"
							placeholder="Enter mandatory comment for this resource"
						/>
						<div className="mt-5 flex justify-end gap-2">
							<button
								type="button"
								onClick={handleCloseEditComment}
								className="btn-secondary"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={handleSaveEditedComment}
								disabled={!editCommentModal.comment.trim()}
								className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
							>
								Save
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
